import { createContext, CSSProperties } from 'react';
import { AppointmentStatus } from '@prisma/client';
import { FormikValues } from 'formik';
import type Stripe from 'stripe';
import {
  Appointment,
  CheckoutFailureData,
  LoadedUser,
  Option,
  PiercingLocation,
  TerminalCheckoutModalState,
} from '../../interfaces';
import { SearchItemButton } from '../../components/global/SearchModal';
import { AppointmentHit, SelectedAppointment } from '../../types/appointments';
import { FormData } from '../../components/global/FormSmallModal';
import { ColoredAlertProps } from '../../components/global/ColoredAlert';
import { CreateAptValues } from '../../utils/form/appointmentForm';
import type { SetupIntentResult } from '../../hooks/marketing/usePaymentMethodCollection';
import { getBackwardsCompatibleSelectedBusiness } from '../../services/user';

export interface AppState<TValues extends FormikValues = any> {
  webviewShouldBeAuthenticated: boolean;
  user: {
    data: LoadedUser | null;
    isLoading: boolean;
    selectedBusiness: string;
  };
  piercingLocations: PiercingLocation[] | null;
  selectedAppointment: SelectedAppointment | null;
  openedAppointment: Appointment | null;
  // What the sidebar should do once it opens, e.g. from the calendar waiver icon.
  openedAppointmentIntent: 'waiver' | null;
  selectedAppointmentUpdated: boolean;
  checkout: {
    checkoutModalOpen: boolean;
    terminalCheckoutModal: TerminalCheckoutModalState;
    checkoutFailureData: CheckoutFailureData | null;
    mostRecentPaymentId: string;
    isInSplitCheckout: boolean;
    guestReceiptPaymentId: string | null;
    pendingGuestReceipt: {
      paymentId: string;
      splitPaymentsGroupId: string | null;
    } | null;
  };
  confirmationModal: {
    status: 'idle' | 'awaiting' | 'resolved' | 'rejected';
    resolve: ((response: boolean) => void) | null;
    title: string;
    message: string;
    dismissible?: boolean;
    positiveText?: string;
    negativeText?: string;
    isSmall?: boolean;
    hideCloseButton?: boolean;
    noWrapButtonsText?: boolean;
  };
  loadingModal: {
    header: string;
    message: string;
    status: 'idle' | 'loading';
  };
  isSubModalDismissed: boolean;
  waiversTablePage: number;
  searchModal: {
    isOpen: boolean;
    type: 'chat' | 'customer-chat' | 'appointment';
    appointmentsOnly?: boolean;
    placeholder?: string;
    itemButton?: SearchItemButton<AppointmentHit> | null;
    sort?: {
      by: string;
      direction: 'asc' | 'desc';
    } | null;
    appointmentStatus: AppointmentStatus | 'ALL' | null;
  };
  unreadConversations: Record<string, number>;
  awaitedFormModal: {
    status: 'idle' | 'awaiting' | 'resolved' | 'rejected';
    resolve: ((response: TValues | null) => void) | null;
    form: FormData<TValues> | null;
    alert?: ColoredAlertProps;
    maxWidth?: CSSProperties['width'];
    allowUntouched?: boolean;
  };
  paymentMethodModal: {
    status: 'idle' | 'awaiting' | 'resolved' | 'rejected';
    resolve: ((result: SetupIntentResult | null) => void) | null;
    setupIntent: Stripe.SetupIntent | null;
    title?: string;
    buttonText?: string;
  };
  selectedSplitPaymentsGroupId: string | null;
  isNewAppointmentFormOpen: boolean;
  initialAppointmentForm: Partial<CreateAptValues> | null;
  depositCheckout: {
    appointmentId: string | null;
    onRefresh?: () => void | Promise<void>;
    allowTerminal?: boolean;
  };
}

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>; // Replace 'any' with the actual action type if you have one
}

const initialSearchModalState: Required<AppState['searchModal']> = {
  isOpen: false,
  appointmentsOnly: false,
  placeholder: 'Search for appointments or customers',
  itemButton: null,
  sort: null,
  appointmentStatus: null,
  type: 'appointment',
};

export const initialState: AppState = {
  webviewShouldBeAuthenticated: false,
  user: {
    data: null,
    isLoading: true,
    selectedBusiness: getBackwardsCompatibleSelectedBusiness(),
  },
  piercingLocations: null,
  selectedAppointment: null,
  openedAppointment: null,
  openedAppointmentIntent: null,
  selectedAppointmentUpdated: false,
  checkout: {
    checkoutModalOpen: false,
    terminalCheckoutModal: {
      isOpen: false,
      title: '',
      message: '',
      buttonText: '',
      status: 'idle',
    },
    checkoutFailureData: null,
    mostRecentPaymentId: '',
    isInSplitCheckout: false,
    guestReceiptPaymentId: null,
    pendingGuestReceipt: null,
  },
  confirmationModal: {
    status: 'idle',
    title: 'Confirm',
    message: 'Are you sure you want to continue?',
    dismissible: true,
    positiveText: 'Continue',
    negativeText: 'Cancel',
    resolve: null,
    isSmall: false,
    hideCloseButton: false,
  },
  awaitedFormModal: {
    status: 'idle',
    resolve: null,
    form: null,
    alert: undefined,
    maxWidth: undefined,
    allowUntouched: false,
  },
  paymentMethodModal: {
    status: 'idle',
    resolve: null,
    setupIntent: null,
    title: undefined,
    buttonText: undefined,
  },
  loadingModal: {
    header: 'Loading...',
    message: 'This could take a minute, please do not refresh the page',
    status: 'idle',
  },
  isSubModalDismissed: false,
  waiversTablePage: 0,
  searchModal: initialSearchModalState,
  unreadConversations: {},
  selectedSplitPaymentsGroupId: null,
  isNewAppointmentFormOpen: false,
  initialAppointmentForm: null,
  depositCheckout: {
    appointmentId: null,
    onRefresh: undefined,
    allowTerminal: true,
  },
};

export type AppStateAction =
  | { type: 'SET_USER'; payload: Partial<AppState['user']> }
  | { type: 'SET_PIERCING_LOCATIONS'; payload: PiercingLocation[] }
  | { type: 'SET_LENGTH_OPTIONS'; payload: Option[] }
  | { type: 'SET_SELECTED_APPOINTMENT_UPDATED'; payload: boolean }
  | { type: 'SET_CHECKOUT_MODAL_OPEN'; payload: boolean }
  | { type: 'SET_CHECKOUT_FAILURE_DATA'; payload: CheckoutFailureData | null }
  | { type: 'SET_TERMINAL_CHECKOUT_MODAL'; payload: TerminalCheckoutModalState }
  | { type: 'SET_OPENED_APPOINTMENT'; payload: Appointment | null }
  | { type: 'SET_OPENED_APPOINTMENT_INTENT'; payload: 'waiver' | null }
  | { type: 'SET_WAIVERS_TABLE_PAGE'; payload: number }
  | {
      type: 'SET_SELECTED_APPOINTMENT';
      payload: SelectedAppointment | null;
    }
  | { type: 'SET_SELECTED_BUSINESS'; payload: string }
  | { type: 'SET_MOST_RECENT_PAYMENTID'; payload: string }
  | { type: 'SET_GUEST_RECEIPT_PAYMENT_ID'; payload: string | null }
  | {
      type: 'SET_PENDING_GUEST_RECEIPT';
      payload: AppState['checkout']['pendingGuestReceipt'];
    }
  | {
      type: 'SET_CONFIRMATION_MODAL';
      payload: Partial<AppState['confirmationModal']>;
    }
  | {
      type: 'SET_LOADING_MODAL';
      payload: Partial<AppState['loadingModal']>;
    }
  | { type: 'SET_SUB_MODAL_DISMISSED'; payload: boolean }
  | { type: 'SET_SEARCH_MODAL'; payload: Partial<AppState['searchModal']> }
  | { type: 'SET_UNREAD_CONVERSATIONS'; payload: Record<string, number> }
  | {
      type: 'SET_AWAITED_FORM_MODAL';
      payload: Partial<AppState['awaitedFormModal']>;
    }
  | {
      type: 'SET_PAYMENT_METHOD_MODAL';
      payload: Partial<AppState['paymentMethodModal']>;
    }
  | {
      type: 'SET_SELECTED_SPLIT_PAYMENTS_GROUP_ID';
      payload: AppState['selectedSplitPaymentsGroupId'];
    }
  | {
      type: 'SET_IS_IN_SPLIT_CHECKOUT';
      payload: AppState['checkout']['isInSplitCheckout'];
    }
  | {
      type: 'SET_IS_NEW_APPOINTMENT_FORM_OPEN';
      payload: AppState['isNewAppointmentFormOpen'];
    }
  | {
      type: 'SET_INITIAL_APPOINTMENT_FORM';
      payload: Partial<CreateAptValues> | null;
    }
  | {
      type: 'SET_WEBVIEW_SHOULD_BE_AUTHENTICATED';
      payload: boolean;
    }
  | {
      type: 'SET_DEPOSIT_CHECKOUT_APPOINTMENT_ID';
      payload: {
        appointmentId: string | null;
        onRefresh?: () => void | Promise<void>;
        allowTerminal?: boolean;
      };
    };

// Providing an initial value for dispatch as well (a no-op function in this case)
const defaultContextValue: AppContextType = {
  state: initialState,
  dispatch: () => {},
};

export const AppContext = createContext<AppContextType>(defaultContextValue);
